import {useCan} from '@/shared/hooks/use-permissions'
import {requirePermission} from '@/modules/auth/permissions'
import {dialog} from '@/modules/layout'
import {useGetInfiniteTradeNames} from '@/modules/products/trade-names/api'
import {TradeNamesFilters, TradeNamesTable} from '@/modules/products/trade-names/components'
import {SelectTradeNameTypeDialog} from '@/modules/products/trade-names/components/select-trade-name-type-dialog'
import {TradeNamesSearchSchema} from '@/modules/products/trade-names/schemas'
import {ResourcesIndexPageHeader} from '@/shared/components/ui/resources-index-page-header'
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {type OnChangeFn, type SortingState} from '@tanstack/react-table'
import {ResourcesIndexPageTable} from '@/shared/components/ui/resources-index-page-table'
import {useCallback} from 'react'

export const Route = createFileRoute('/configuration/products/trade-names/')({
  beforeLoad: requirePermission('catalogue.view'),
  component: TradeNamesPage,
  validateSearch: TradeNamesSearchSchema,
})

const LIMIT = 50

async function onAddNew() {
  const id = 'select-trade-name-type'
  await dialog.open(id, {
    id,
    children: <SelectTradeNameTypeDialog />,
  })
}

function TradeNamesPage() {
  const canManageCatalogue = useCan('catalogue.manage')
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [sort] = search.sort

  const infinite = useGetInfiniteTradeNames({
    limit: LIMIT,
    filters: {
      status: [],
      search: search.filters?.search,
      substances: search.filters?.substances,
      types: search.filters?.types,
      forms: search.filters?.forms,
    },
    sort: {
      dir: sort?.desc ? 'desc' : 'asc',
      field: sort?.id || 'createdAt',
    },
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infinite

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    updaterOrValue => {
      const newValue = typeof updaterOrValue === 'function' ? updaterOrValue(search.sort) : updaterOrValue
      navigate({to: '.', search: prev => ({...prev, sort: newValue})})
    },
    [navigate, search.sort]
  )

  return (
    <>
      <ResourcesIndexPageHeader {...(canManageCatalogue ? {onAddNew} : {})}>
        <TradeNamesFilters />
      </ResourcesIndexPageHeader>
      <ResourcesIndexPageTable>
        <TradeNamesTable
          virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
          table={{onSortingChange, state: {sorting: search.sort}}}
        />
      </ResourcesIndexPageTable>
    </>
  )
}
